import { MemberStatistic } from '../../../services/api/statistics/statistics';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import formatMoney from '../../../utils/formatMoney';
import {
  BenchmarkStatistics,
  DroppedReason,
} from '@tassomutuo-backoffice/types';
import React from 'react';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

interface Props {
  teamMembersStatistics: MemberStatistic[];
  benchmarkStatistics?: BenchmarkStatistics;
  isPrinting: boolean;
}

const dropAlertMapLabels: Record<
  string,
  { label: string; description: string }
> = {
  'Non si è mai presentato all’appuntamento': {
    label: "Mai presentato all'app.",
    description: 'Il consulente ha difficoltà ad incontrare le lead',
  },
  'Appuntamento Disdetto': {
    label: 'Appuntamento disdetto',
    description:
      'Il consulente ha difficoltà a fissare gli appuntamenti con il cliente',
  },
  'Appuntamento andato male / Requisiti Bocciati': {
    label: 'Appuntamento andato male',
    description:
      'Il consulente ha un tasso di chiusura per non fattibilità più alto del benchmark di riferimento',
  },
  'Andato con un’altra offerta / Non vuole pagare la provvigione': {
    label: "Andato con un'altra offerta",
    description:
      "Il consulente ha più clienti che decidono di non effettuare l'operazione con lui, rispetto al benchmark di riferimento",
  },
  'Dilungamento / Altri Problemi': {
    label: 'Dilungamento/altri problemi',
    description:
      "Il consulente non riesce a mantenere il contatto con molti clienti dopo l'appuntamento",
  },
  'Pratica Bloccata dalla Banca': {
    label: 'Pratica bloccata',
    description:
      'Il consulente ha molte pratiche che non è riuscito a concludere',
  },
};

const DashboardTeamMemberStatistics = ({
  teamMembersStatistics,
  benchmarkStatistics,
  isPrinting,
}: Props) => {
  const getDropAlert = (
    droppedReasons: DroppedReason
  ): { label: string; description: string; value: number }[] => {
    const droppedReasonAlert = droppedReasons.percentages.filter((p) => {
      // Esclude 'Altro' reason dalla visualizzazione degli alert
      if (p.label === 'Altro') return false;

      const benchmarkValue =
        benchmarkStatistics?.benchmarkStatisticsAllPeriod.droppedReasons?.percentages.find(
          (dr) => dr.label === p.label
        )?.value;

      if (benchmarkValue === undefined) {
        return false;
      }

      const difference = p.value - benchmarkValue;
      // Mostra l'alert solo se la differenza è maggiore di 5 punti percentuali
      if (difference <= 5) {
        return false;
      }
      return p.value - benchmarkValue > 0;
    });

    return droppedReasonAlert.map((p) => {
      const benchmarkValue =
        benchmarkStatistics?.benchmarkStatisticsAllPeriod.droppedReasons?.percentages.find(
          (dr) => dr.label === p.label
        )?.value || 0;

      const values = dropAlertMapLabels[p.label] || {
        label: p.label,
        description: '',
      };
      return { ...values, value: p.value - benchmarkValue };
    });
  };

  const tableColumns: GridColDef<MemberStatistic>[] = [
    {
      field: 'email',
      headerName: 'Consulente',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.user.email,
    },
    {
      field: 'dropAlert',
      headerName: 'Avvisi',
      sortable: false,
      minWidth: 200,
      renderCell: ({ row }) => {
        const droppedReasons = row.statistics?.droppedReasons;

        if (!droppedReasons || Object.keys(droppedReasons).length === 0) {
          return '-';
        }

        const dropAlerts = getDropAlert(droppedReasons);

        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
              height: '100%',
              maxWidth: '100%',
            }}
          >
            {dropAlerts.map((alert, index) => (
              <Tooltip
                key={`${alert.label}-${index}`}
                title={
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      color: '#333',
                      padding: '8px 16px',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      maxWidth: '280px',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'primary.main',
                        lineHeight: 1.3,
                      }}
                    >
                      Drop: {alert.label}
                    </Box>
                    <Box
                      sx={{
                        fontSize: '12px',
                        color: 'primary.main',
                        lineHeight: 1.4,
                      }}
                    >
                      {alert.description}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#F44336',
                      }}
                    >
                      <TrendingDownIcon />
                      <Typography
                        variant="body2"
                        color="#F44336"
                        fontWeight="bold"
                      >
                        +{alert.value.toFixed(1)}% Rispetto al benchmark
                      </Typography>
                    </Box>
                  </Box>
                }
                placement="bottom"
                arrow={false}
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'transparent',
                      padding: 0,
                      '& .MuiTooltip-arrow': {
                        display: 'none',
                      },
                    },
                  },
                }}
              >
                <Chip
                  label={alert.label}
                  size="small"
                  sx={{
                    backgroundColor: '#FEE4E2',
                    color: '#D92D20',
                    fontSize: '0.75rem',
                    height: '24px',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      px: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'statistics.numberOfReceivedLeads',
      headerName: 'Lead ricevute',
      minWidth: 150,
      valueGetter: (params) => {
        const value =
          params.row.statistics?.statisticsAllPeriod.numberOfReceivedLeads;

        return value || 'N/A';
      },
    },
    {
      field: 'statistics.crEstimatedMortgages',
      headerName: 'CR Mutui/Lead',
      flex: 1,
      valueGetter: (params) => {
        const value =
          params.row.statistics?.statisticsAllPeriod.crEstimatedMortgages;

        return value ? `${value}%` : 'N/A';
      },
    },
    {
      field: 'benchmark.crEstimatedMortgages',
      headerName: 'CR Mutui/Lead Benchmark',
      flex: 1,
      valueGetter: (params) => {
        const value =
          benchmarkStatistics?.benchmarkStatisticsAllPeriod.statistics
            .crEstimatedMortgages;

        return value ? `${value}%` : 'N/A';
      },
    },
    {
      field: 'deltaCrEstimatedMortgages',
      headerName: 'Delta',
      minWidth: 100,
      valueGetter: (params) => {
        const cr =
          params.row.statistics?.statisticsAllPeriod.crEstimatedMortgages;
        const crBench =
          benchmarkStatistics?.benchmarkStatisticsAllPeriod.statistics
            .crEstimatedMortgages;

        if (!cr || !crBench) return 'N/A';

        return `${((cr / crBench - 1) * 100).toFixed(2)}%`;
      },
    },

    /*{
      field: 'statistics.revenuePerLead',
      headerName: 'Revenue/Lead',
      flex: 1,
      valueGetter: (params) => {
        const value = params.row.statistics?.statisticsAllPeriod.revenuePerLead;
        return value ? formatMoney(value) : 'N/A';
      },
    },*/

    {
      field: 'statistics.effectiveRevenue',
      headerName: 'Fatturato totale',
      flex: 1,
      valueGetter: (params) => {
        const value =
          params.row.statistics?.statisticsAllPeriod.effectiveRevenue;
        return value ? formatMoney(value) : 0;
      },
    },
    {
      field: 'statistics.lostRevenue',
      headerName: 'Fatturato perso',
      flex: 1,
      valueGetter: (params) => {
        const value = params.row.statistics?.statisticsAllPeriod.lostRevenue;
        return value ? formatMoney(value) : 0;
      },
    },

    /*{
      field: 'statistics.leadsWonCount',
      headerName: 'Lead vinte',
      flex: 1,
      valueGetter: (params) => {
        const value = params.row.statistics?.statisticsAllPeriod.leadsWonCount;
        return value || 0;
      },
    },*/
  ];

  return (
    <Box
      sx={{
        backgroundColor: isPrinting ? 'white' : '#FAFAFA',
        padding: 2,
        my: 6,
        borderRadius: '8px',
        border: '1px solid rgba(224, 224, 224, 1)',
      }}
    >
      <DataGrid
        getRowId={(r) => r.user.id}
        columns={tableColumns}
        rows={teamMembersStatistics}
        disableRowSelectionOnClick
        autoHeight
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': {
            border: '1px solid rgba(224, 224, 224, 1)',
            color: '#717680',
            fontWeight: '700',
            py: 1,
          },

          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            color: 'secondary.main',
            fontSize: '18px',
          },
        }}
      />
    </Box>
  );
};

export default DashboardTeamMemberStatistics;
